import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ChevronRight, Plus, Pencil, Trash2, Copy, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getProject, updateProject, type Project } from '@/api/projects'
import { MOCK_BASE_DOMAIN } from '@/lib/constants'
import { useAuth } from '@/hooks/useAuth'
import {
  listEndpoints,
  createEndpoint,
  updateEndpoint,
  deleteEndpoint,
  HTTP_METHODS,
  type Endpoint,
  type HttpMethod,
} from '@/api/endpoints'
import { ApiError } from '@/api/client'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
// ── Method badge ───────────────────────────────────────────────────────────

const METHOD_COLORS: Record<HttpMethod, string> = {
  GET:     'bg-blue-100 text-blue-700 border-blue-200',
  POST:    'bg-green-100 text-green-700 border-green-200',
  PUT:     'bg-orange-100 text-orange-700 border-orange-200',
  PATCH:   'bg-purple-100 text-purple-700 border-purple-200',
  DELETE:  'bg-red-100 text-red-700 border-red-200',
  HEAD:    'bg-gray-100 text-gray-600 border-gray-200',
}

function MethodBadge({ method }: { method: HttpMethod }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-2 py-0.5 text-xs font-mono font-semibold',
        METHOD_COLORS[method],
      )}
    >
      {method}
    </span>
  )
}

// ── Endpoint form schema ───────────────────────────────────────────────────

const endpointSchema = z.object({
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'] as const),
  path: z.string().min(1).startsWith('/'),
  priority: z.coerce.number().int().default(0),
  description: z.string().max(500).optional(),
})

type EndpointFormValues = z.infer<typeof endpointSchema>

// ── Copy button ───────────────────────────────────────────────────────────

function CopyButton({ value, className }: { value: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation()

  const handleCopy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <button
      onClick={handleCopy}
      title={t('common.copy')}
      className={cn('inline-flex items-center text-muted-foreground hover:text-foreground transition-colors', className)}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <tr>
      <td className="p-3"><div className="h-5 w-14 rounded bg-muted animate-pulse" /></td>
      <td className="p-3"><div className="h-4 w-32 rounded bg-muted animate-pulse" /></td>
      <td className="p-3 hidden md:table-cell"><div className="h-4 w-48 rounded bg-muted animate-pulse" /></td>
      <td className="p-3"><div className="h-4 w-6 rounded bg-muted animate-pulse mx-auto" /></td>
      <td className="p-3" />
    </tr>
  )
}

// ── Create endpoint dialog ─────────────────────────────────────────────────

function CreateEndpointDialog({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: (data: EndpointFormValues) => createEndpoint(projectId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['endpoints', projectId] })
      setOpen(false)
      reset()
      toast.success(t('project.createEndpoint.success'))
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : t('project.createEndpoint.failed'))
    },
  })

  const { register, handleSubmit, control, reset, formState: { errors, isSubmitting } } =
    useForm<EndpointFormValues>({
      resolver: zodResolver(endpointSchema),
      defaultValues: { method: 'GET', priority: 0 },
    })

  const handleOpenChange = (v: boolean) => {
    setOpen(v)
    if (!v) { reset(); setServerError(null) }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          {t('project.addEndpoint')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('project.createEndpoint.title')}</DialogTitle>
          <DialogDescription>{t('project.createEndpoint.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-4">
          {serverError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {serverError}
            </p>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>{t('project.endpoint.method')}</Label>
              <Controller
                name="method"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HTTP_METHODS.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="path">{t('project.endpoint.path')}</Label>
              <Input id="path" placeholder={t('project.createEndpoint.pathPlaceholder')} {...register('path')} />
              {errors.path && (
                <p className="text-sm text-destructive">{errors.path.message}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="priority">{t('project.endpoint.priority')}</Label>
            <Input id="priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t('project.createEndpoint.description')}</Label>
            <Textarea id="description" placeholder={t('project.createEndpoint.descriptionPlaceholder')} rows={2} {...register('description')} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {mutation.isPending ? t('common.creating') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ── Edit endpoint dialog ───────────────────────────────────────────────────

function EditEndpointDialog({ endpoint, projectId }: { endpoint: Endpoint; projectId: string }) {
  const [open, setOpen] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: (data: EndpointFormValues) => updateEndpoint(endpoint.id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['endpoints', projectId] })
      setOpen(false)
      toast.success(t('project.editEndpoint.success'))
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : t('project.editEndpoint.failed'))
    },
  })

  const { register, handleSubmit, control, formState: { errors } } =
    useForm<EndpointFormValues>({
      resolver: zodResolver(endpointSchema),
      defaultValues: {
        method: endpoint.method,
        path: endpoint.path,
        priority: endpoint.priority,
        description: endpoint.description ?? '',
      },
    })

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setServerError(null) }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('project.editEndpoint.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-4">
          {serverError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {serverError}
            </p>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>{t('project.endpoint.method')}</Label>
              <Controller
                name="method"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HTTP_METHODS.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="edit-path">{t('project.endpoint.path')}</Label>
              <Input id="edit-path" {...register('path')} />
              {errors.path && (
                <p className="text-sm text-destructive">{errors.path.message}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-priority">{t('project.endpoint.priority')}</Label>
            <Input id="edit-priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">{t('project.endpoint.description')}</Label>
            <Textarea id="edit-description" rows={2} {...register('description')} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t('common.updating') : t('common.update')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ── Delete endpoint dialog ─────────────────────────────────────────────────

function DeleteEndpointButton({ endpoint, projectId }: { endpoint: Endpoint; projectId: string }) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: () => deleteEndpoint(endpoint.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['endpoints', projectId] })
      setOpen(false)
      toast.success(t('project.deleteEndpoint.success'))
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('project.deleteEndpoint.failed'))
    },
  })

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!mutation.isPending) { setOpen(v); if (!v) setError(null) } }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('project.deleteEndpoint.title')}</DialogTitle>
          <DialogDescription>
            <span className="font-mono font-medium">{endpoint.method} {endpoint.path}</span> {t('project.deleteEndpoint.description')}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Edit project dialog(名前・説明文) ───────────────────────────────────────

const editProjectSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
})

type EditProjectFormValues = z.infer<typeof editProjectSchema>

function EditProjectDialog({ project }: { project: Project }) {
  const [open, setOpen] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: (data: EditProjectFormValues) =>
      updateProject(project.id, { name: data.name, description: data.description ?? null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      setOpen(false)
      toast.success(t('project.editProject.success'))
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : t('project.editProject.failed'))
    },
  })

  const { register, handleSubmit, formState: { errors } } = useForm<EditProjectFormValues>({
    resolver: zodResolver(editProjectSchema),
    defaultValues: {
      name: project.name,
      description: project.description ?? '',
    },
  })

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setServerError(null) }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('project.editProject.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-4">
          {serverError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {serverError}
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="edit-project-name">{t('dashboard.createDialog.name')}</Label>
            <Input id="edit-project-name" {...register('name')} />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-project-description">{t('dashboard.createDialog.description')}</Label>
            <Textarea
              id="edit-project-description"
              rows={3}
              placeholder={t('dashboard.createDialog.descriptionPlaceholder')}
              {...register('description')}
            />
            <p className="text-xs text-muted-foreground">{t('project.editProject.descriptionHint')}</p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t('common.updating') : t('common.update')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

export function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { t } = useTranslation()
  const { user } = useAuth()

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
  })

  const { data: endpoints, isLoading: endpointsLoading, isError } = useQuery({
    queryKey: ['endpoints', projectId],
    queryFn: () => listEndpoints(projectId!),
    enabled: !!projectId,
  })

  if (!projectId) return null

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-5xl p-6 space-y-6">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/dashboard" className="hover:text-foreground transition-colors">{t('project.projects')}</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground font-medium">
            {projectLoading ? '...' : project?.name}
          </span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">
                {projectLoading ? t('common.loading') : project?.name}
              </h1>
              {project && (
                <Badge variant="secondary" className="font-mono text-xs">
                  {project.slug}
                </Badge>
              )}
              {project && <EditProjectDialog project={project} />}
            </div>
            {project?.description ? (
              <p className="text-sm text-muted-foreground">{project.description}</p>
            ) : project ? (
              <p className="text-sm text-muted-foreground italic">{t('project.noDescription')}</p>
            ) : null}
            {project && (
              <p className="text-xs text-muted-foreground font-mono flex items-center gap-1.5">
                {t('project.mockUrl')}:
                <span className="text-foreground">
                  {user?.domain ? `${user.domain}--${project.slug}` : project.slug}.{MOCK_BASE_DOMAIN}
                </span>
                <CopyButton value={`${user?.domain ? `${user.domain}--${project.slug}` : project.slug}.${MOCK_BASE_DOMAIN}`} />
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" asChild>
              <Link to="/dashboard"><ArrowLeft className="mr-2 h-4 w-4" />{t('common.back')}</Link>
            </Button>
            <CreateEndpointDialog projectId={projectId} />
          </div>
        </div>

        {/* Endpoint table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">{t('project.endpoint.method')}</TableHead>
                <TableHead>{t('project.endpoint.path')}</TableHead>
                <TableHead className="hidden md:table-cell">{t('project.endpoint.description')}</TableHead>
                <TableHead className="w-20 text-center">{t('project.endpoint.priority')}</TableHead>
                <TableHead className="w-24 text-right">{t('project.endpoint.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {endpointsLoading && (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              )}
              {isError && (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-destructive">
                    {t('project.endpoint.loadFailed')}
                  </TableCell>
                </TableRow>
              )}
              {endpoints && endpoints.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    {t('project.endpoint.none')}
                  </TableCell>
                </TableRow>
              )}
              {endpoints?.map((ep) => (
                <TableRow key={ep.id}>
                  <TableCell>
                    <MethodBadge method={ep.method} />
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/projects/${projectId}/endpoints/${ep.id}`}
                      className="font-mono text-sm hover:underline"
                    >
                      {ep.path}
                    </Link>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                    {ep.description ?? '—'}
                  </TableCell>
                  <TableCell className="text-center text-sm">{ep.priority}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <EditEndpointDialog endpoint={ep} projectId={projectId} />
                      <DeleteEndpointButton endpoint={ep} projectId={projectId} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

      </div>
    </div>
  )
}
